/**
 * Posts API
 *
 * API functions for blog/news posts management
 */

import axios from "./axiosInstance";
import config from "./config";

const BASE_URL = config.BASE_URL;

// ============================================
// TYPES
// ============================================

export type PostStatus = "draft" | "published" | "archived";

export interface Post {
  id: string;
  tenant_id: string;
  title: string;
  slug: string;
  content: string | null;
  excerpt: string | null;
  featured_image: string | null;
  author_id: string | null;
  author?: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
  } | null;
  status: PostStatus;
  published_at: string | null;
  categories: string[];
  seo_title: string | null;
  seo_description: string | null;
  created_at: string;
  updated_at: string;
}

export interface PostsListResponse {
  success: boolean;
  data: Post[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  message?: string;
  /** 'BLOG_DISABLED' when the club has its public blog turned off (404). */
  code?: string;
}

export interface PostResponse {
  success: boolean;
  data: Post;
  message?: string;
}

export interface CategoriesResponse {
  success: boolean;
  data: {
    categories: string[];
    usedCategories: string[];
    defaultCategories: string[];
  };
  message?: string;
}

export interface CreatePostData {
  title: string;
  slug?: string;
  content?: string;
  excerpt?: string;
  featured_image?: string;
  status?: PostStatus;
  categories?: string[];
  seo_title?: string;
  seo_description?: string;
}

export interface UpdatePostData {
  title?: string;
  slug?: string;
  content?: string;
  excerpt?: string;
  featured_image?: string;
  status?: PostStatus;
  categories?: string[];
  seo_title?: string;
  seo_description?: string;
}

export interface PostFilters {
  status?: PostStatus;
  category?: string;
  search?: string;
  sort_by?: "created_at" | "updated_at" | "published_at" | "title";
  sort_order?: "asc" | "desc";
  page?: number;
  limit?: number;
}

// ============================================
// HELPERS
// ============================================

const getPostsHeaders = (token: string) => ({
  "Content-Type": "application/json",
  authorization: `Bearer ${token}`,
});

const getPublicHeaders = () => ({
  "Content-Type": "application/json",
});

// ============================================
// POSTS CRUD APIs
// ============================================

/**
 * Get all posts with optional filters and pagination (admin)
 */
export const getAllPosts = async (
  token: string,
  filters?: PostFilters
): Promise<PostsListResponse> => {
  try {
    const params = new URLSearchParams();
    if (filters?.status) params.append("status", filters.status);
    if (filters?.category) params.append("category", filters.category);
    if (filters?.search) params.append("search", filters.search);
    if (filters?.sort_by) params.append("sort_by", filters.sort_by);
    if (filters?.sort_order) params.append("sort_order", filters.sort_order);
    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.limit) params.append("limit", filters.limit.toString());

    const queryString = params.toString();
    const url = `${BASE_URL}/posts${queryString ? `?${queryString}` : ""}`;

    const response = await axios.get(url, {
      headers: getPostsHeaders(token),
    });
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    return {
      success: false,
      data: [],
      pagination: { page: 1, limit: 50, total: 0, totalPages: 0 },
      message: err.response?.data?.message || err.message || "Failed to fetch posts",
    };
  }
};

/**
 * Get public posts (for public website, no auth required)
 */
export const getPublicPosts = async (
  filters?: Omit<PostFilters, "status">
): Promise<PostsListResponse> => {
  try {
    const params = new URLSearchParams();
    if (filters?.category) params.append("category", filters.category);
    if (filters?.search) params.append("search", filters.search);
    if (filters?.page) params.append("page", filters.page.toString());
    if (filters?.limit) params.append("limit", filters.limit.toString());

    const queryString = params.toString();
    const url = `${BASE_URL}/posts/public${queryString ? `?${queryString}` : ""}`;

    const response = await axios.get(url, {
      headers: getPublicHeaders(),
    });
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string; code?: string } }; message?: string };
    return {
      success: false,
      data: [],
      pagination: { page: 1, limit: 10, total: 0, totalPages: 0 },
      message: err.response?.data?.message || err.message || "Failed to fetch posts",
      code: err.response?.data?.code,
    };
  }
};

/**
 * Get a single post by ID (admin)
 */
export const getPostById = async (
  token: string,
  postId: string
): Promise<PostResponse> => {
  try {
    const response = await axios.get(`${BASE_URL}/posts/${postId}`, {
      headers: getPostsHeaders(token),
    });
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    return {
      success: false,
      data: {} as Post,
      message: err.response?.data?.message || err.message || "Failed to fetch post",
    };
  }
};

/**
 * Get a single post by slug (public)
 */
export const getPostBySlug = async (slug: string): Promise<PostResponse> => {
  try {
    const response = await axios.get(`${BASE_URL}/posts/slug/${slug}`, {
      headers: getPublicHeaders(),
    });
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    return {
      success: false,
      data: {} as Post,
      message: err.response?.data?.message || err.message || "Failed to fetch post",
    };
  }
};

/**
 * Create a new post
 */
export const createPost = async (
  token: string,
  postData: CreatePostData
): Promise<PostResponse> => {
  try {
    const response = await axios.post(`${BASE_URL}/posts`, postData, {
      headers: getPostsHeaders(token),
    });
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    return {
      success: false,
      data: {} as Post,
      message: err.response?.data?.message || err.message || "Failed to create post",
    };
  }
};

/**
 * Update an existing post
 */
export const updatePost = async (
  token: string,
  postId: string,
  postData: UpdatePostData
): Promise<PostResponse> => {
  try {
    const response = await axios.put(`${BASE_URL}/posts/${postId}`, postData, {
      headers: getPostsHeaders(token),
    });
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    return {
      success: false,
      data: {} as Post,
      message: err.response?.data?.message || err.message || "Failed to update post",
    };
  }
};

/**
 * Delete a post
 */
export const deletePost = async (
  token: string,
  postId: string
): Promise<{ success: boolean; message?: string }> => {
  try {
    const response = await axios.delete(`${BASE_URL}/posts/${postId}`, {
      headers: getPostsHeaders(token),
    });
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    return {
      success: false,
      message: err.response?.data?.message || err.message || "Failed to delete post",
    };
  }
};

/**
 * Publish a post
 */
export const publishPost = async (
  token: string,
  postId: string
): Promise<PostResponse> => {
  try {
    const response = await axios.post(`${BASE_URL}/posts/${postId}/publish`, {}, {
      headers: getPostsHeaders(token),
    });
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    return {
      success: false,
      data: {} as Post,
      message: err.response?.data?.message || err.message || "Failed to publish post",
    };
  }
};

/**
 * Unpublish a post (revert to draft)
 */
export const unpublishPost = async (
  token: string,
  postId: string
): Promise<PostResponse> => {
  try {
    const response = await axios.post(`${BASE_URL}/posts/${postId}/unpublish`, {}, {
      headers: getPostsHeaders(token),
    });
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    return {
      success: false,
      data: {} as Post,
      message: err.response?.data?.message || err.message || "Failed to unpublish post",
    };
  }
};

// ============================================================================
// Comments + reactions (post engagement)
// ============================================================================

export type ReactionType = "like" | "love" | "laugh" | "wow" | "celebrate" | "sad";

export const REACTION_LABELS: Record<ReactionType, { emoji: string; label: string }> = {
  like: { emoji: "👍", label: "Like" },
  love: { emoji: "❤️", label: "Love" },
  laugh: { emoji: "😂", label: "Laugh" },
  wow: { emoji: "😮", label: "Wow" },
  celebrate: { emoji: "🎉", label: "Celebrate" },
  sad: { emoji: "😢", label: "Sad" },
};

export interface PostComment {
  id: string;
  post_id: string;
  author_user_id: string | null;
  author_membership_id: string | null;
  author_name: string | null;
  author_email: string | null;
  body: string;
  status: "pending" | "approved" | "hidden";
  approved_at: string | null;
  hidden_at: string | null;
  hidden_reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface PostReactionsSummary {
  counts: Record<ReactionType, number>;
  total: number;
  my_reaction: ReactionType | null;
}

const noTokenHeaders = () => ({
  "Content-Type": "application/json",
  "x-tenant-id": (() => {
    const parts = window.location.pathname.split("/").filter(Boolean);
    return parts[0] || "";
  })(),
});

export const listPostComments = async (
  token: string | null,
  postId: string
): Promise<{ success: boolean; data?: PostComment[]; error?: string }> => {
  try {
    const headers = token ? getPostsHeaders(token) : noTokenHeaders();
    const response = await axios.get(`${BASE_URL}/posts/${postId}/comments`, { headers });
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

export const createPostComment = async (
  token: string,
  postId: string,
  body: string
) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/posts/${postId}/comments`,
      { body },
      { headers: getPostsHeaders(token) }
    );
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

export const approvePostComment = async (token: string, postId: string, commentId: string) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/posts/${postId}/comments/${commentId}/approve`,
      {},
      { headers: getPostsHeaders(token) }
    );
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

export const hidePostComment = async (
  token: string,
  postId: string,
  commentId: string,
  reason?: string
) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/posts/${postId}/comments/${commentId}/hide`,
      { reason },
      { headers: getPostsHeaders(token) }
    );
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

export const listPostReactions = async (
  token: string | null,
  postId: string
): Promise<{ success: boolean; data?: PostReactionsSummary; error?: string }> => {
  try {
    const headers = token ? getPostsHeaders(token) : noTokenHeaders();
    const response = await axios.get(`${BASE_URL}/posts/${postId}/reactions`, { headers });
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

export const setPostReaction = async (
  token: string,
  postId: string,
  reaction_type: ReactionType
) => {
  try {
    const response = await axios.post(
      `${BASE_URL}/posts/${postId}/reactions`,
      { reaction_type },
      { headers: getPostsHeaders(token) }
    );
    return response.data;
  } catch (error: any) {
    return { success: false, error: error.response?.data?.error || error.message };
  }
};

/**
 * Get available categories
 */
export const getCategories = async (
  token: string
): Promise<CategoriesResponse> => {
  try {
    const response = await axios.get(`${BASE_URL}/posts/categories`, {
      headers: getPostsHeaders(token),
    });
    return response.data;
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } }; message?: string };
    return {
      success: false,
      data: {
        categories: [],
        usedCategories: [],
        defaultCategories: [],
      },
      message: err.response?.data?.message || err.message || "Failed to fetch categories",
    };
  }
};
